import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faUser,
} from '@fortawesome/free-solid-svg-icons';
import { BarWave } from 'react-cssfx-loading';
import { useGetUserQuery, useSubmitUserMutation } from '../../redux/apiSlice';

const UserModal = ({
  isOpen, onClose, userId, mode = 'create',
}) => {
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    phone_number: '',
    department: '',
    position: '',
    category: '',
    role: '',
  });

  const [errors, setErrors] = useState({});

  const {
    data: user, isLoading: userLoadingStatus, error: userError, error: userErrorStatus,
  } = useGetUserQuery(userId, {
    skip: !userId || mode === 'create',
  });

  const [submitUser, { isLoading: isSubmitting }] = useSubmitUserMutation();

  useEffect(() => {
    if (user && mode !== 'create') {
      setFormData({
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        department: user.department || '',
        position: user.position || '',
        category: user.category || '',
        role: user.role || '',
      });
    }
  }, [user, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.department.trim()) newErrors.department = 'Department is required';
    if (!formData.position.trim()) newErrors.position = 'Position is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await submitUser(formData).unwrap();
      onClose();
    } catch (error) {
      // Error handling for user submission
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      username: '',
      email: '',
      phone_number: '',
      department: '',
      position: '',
      category: '',
      role: '',
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  // Helper function to get button text
  const getButtonText = () => {
    if (isSubmitting) return 'Saving...';
    if (isCreateMode) return 'Create User';
    return 'Update User';
  };

  // Helper function to render content based on loading/error states
  const renderContent = () => {
    if (userLoadingStatus && mode !== 'create') {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <BarWave color="#6366f1" height="2rem" width="8rem" />
            <p className="mt-4 text-gray-300">Loading user data...</p>
          </div>
        </div>
      );
    }

    if (userErrorStatus && mode !== 'create') {
      return (
        <div className="bg-red-900/20 border border-red-700 text-red-300 px-4 py-3 rounded">
          <h3 className="font-semibold mb-2">Error loading user</h3>
          <p className="text-sm text-red-200">
            {userError?.data?.message || userError?.message || 'Unknown error occurred'}
          </p>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="bg-gray-750 rounded-lg p-4">
          <h3 className="text-lg font-medium text-white mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                Full Name
                {' '}
                <span className="text-red-400">*</span>
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name || ''}
                onChange={handleChange}
                disabled={isViewMode}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:ring-2 focus:outline-none ${
                  errors.name
                    ? 'border-red-500 focus:border-red-400 focus:ring-red-400'
                    : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                } ${
                  isViewMode ? 'bg-gray-600' : 'bg-gray-700'
                }`}
                placeholder="Enter full name"
              />
              {errors.name && (
                <p className="text-red-400 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                Username
                {' '}
                <span className="text-red-400">*</span>
              </label>
              <input
                id="username"
                type="text"
                name="username"
                value={formData.username || ''}
                onChange={handleChange}
                disabled={isViewMode}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:ring-2 focus:outline-none ${
                  errors.username
                    ? 'border-red-500 focus:border-red-400 focus:ring-red-400'
                    : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                } ${
                  isViewMode ? 'bg-gray-600' : 'bg-gray-700'
                }`}
                placeholder="Enter username"
              />
              {errors.username && (
                <p className="text-red-400 text-sm mt-1">{errors.username}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
                {' '}
                <span className="text-red-400">*</span>
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                disabled={isViewMode}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:ring-2 focus:outline-none ${
                  errors.email
                    ? 'border-red-500 focus:border-red-400 focus:ring-red-400'
                    : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                } ${
                  isViewMode ? 'bg-gray-600' : 'bg-gray-700'
                }`}
                placeholder="Enter email address"
              />
              {errors.email && (
                <p className="text-red-400 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-300 mb-2">
                Phone Number
                <input
                  id="phone_number"
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number || ''}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:ring-2 focus:outline-none ${
                    isViewMode ? 'bg-gray-600' : 'bg-gray-700'
                  }`}
                  placeholder="Enter phone number"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Professional Information */}
        <div className="bg-gray-750 rounded-lg p-4">
          <h3 className="text-lg font-medium text-white mb-4">Professional Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-300 mb-2">
                Department
                {' '}
                <span className="text-red-400">*</span>
              </label>
              <input
                id="department"
                type="text"
                name="department"
                value={formData.department || ''}
                onChange={handleChange}
                disabled={isViewMode}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:ring-2 focus:outline-none ${
                  errors.department
                    ? 'border-red-500 focus:border-red-400 focus:ring-red-400'
                    : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                } ${
                  isViewMode ? 'bg-gray-600' : 'bg-gray-700'
                }`}
                placeholder="Enter department"
              />
              {errors.department && (
                <p className="text-red-400 text-sm mt-1">{errors.department}</p>
              )}
            </div>

            <div>
              <label htmlFor="position" className="block text-sm font-medium text-gray-300 mb-2">
                Position
                {' '}
                <span className="text-red-400">*</span>
              </label>
              <input
                id="position"
                type="text"
                name="position"
                value={formData.position || ''}
                onChange={handleChange}
                disabled={isViewMode}
                className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white placeholder-gray-400 focus:ring-2 focus:outline-none ${
                  errors.position
                    ? 'border-red-500 focus:border-red-400 focus:ring-red-400'
                    : 'border-gray-600 focus:border-blue-500 focus:ring-blue-500'
                } ${
                  isViewMode ? 'bg-gray-600' : 'bg-gray-700'
                }`}
                placeholder="Enter position"
              />
              {errors.position && (
                <p className="text-red-400 text-sm mt-1">{errors.position}</p>
              )}
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-300 mb-2">
                Category
                <input
                  id="category"
                  type="text"
                  name="category"
                  value={formData.category || ''}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:ring-2 focus:outline-none ${
                    isViewMode ? 'bg-gray-600' : 'bg-gray-700'
                  }`}
                  placeholder="Enter category"
                />
              </label>
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-300 mb-2">
                Role
                <input
                  id="role"
                  type="text"
                  name="role"
                  value={formData.role || ''}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:ring-2 focus:outline-none ${
                    isViewMode ? 'bg-gray-600' : 'bg-gray-700'
                  }`}
                  placeholder="Enter role"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        {!isViewMode && (
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {getButtonText()}
            </button>
          </div>
        )}
      </form>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-75" />

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-700">
          {/* Header */}
          <div className="bg-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon icon={faUser} className="text-white text-lg" />
                <div>
                  <h2 className="text-lg font-semibold text-white">
                    {isCreateMode && 'Create New User'}
                    {isEditMode && 'Edit User'}
                    {isViewMode && 'User Details'}
                  </h2>
                  <p className="text-gray-300 text-sm">
                    {isCreateMode && 'Add a new user to the system'}
                    {isEditMode && 'Update user information'}
                    {isViewMode && 'View user information'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-300 hover:text-white p-1"
              >
                <FontAwesomeIcon icon={faTimes} className="text-lg" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

UserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string,
  mode: PropTypes.oneOf(['create', 'edit', 'view']),
};

UserModal.defaultProps = {
  userId: null,
  mode: 'create',
};

export default UserModal;
